/* eslint-disable arrow-body-style */
import * as React from 'react';
import { isEmpty, isNil } from 'lodash';

import Tooltip from 'components/Tooltip';
import Spinner from 'components/spinner/Spinner';
import BarGraphLazy from 'components/graphs/echarts/bar-graph/BarGraphLazy';
import AudienceGenderBar from 'components/audience/AudienceGenderBar';
import BarChartSimple from 'components/graphs/bar-chart-simple/BarChartSimple';
import EmptyMessage from 'modules/reporting/components/empty-message/EmptyMessage';

import { abbrNum, getMetricValueInPercentage } from 'utils/helper/common';
import { AUDIENCE_BY_AGE_STYLE_MAPPING, getAudienceData } from 'components/graphs/echarts/bar-graph/utils/bar-graph';
import { __t, applyTranslations } from 'utils/language';

import './campaign-ig-audience-data.scss';

import type { InstagramPostsReportingAudienceComponentData } from 'modules/reporting/types';
import type { BarChartSimpleItem } from 'types';

applyTranslations(require('./campaign-ig-audience-data.i18n.json'));

type Props = {
  audienceData: InstagramPostsReportingAudienceComponentData,
  type?: 'posts' | 'stories' | 'reels'
};

const AUDIENCE_BY_AGE_X_AXIS_LABELS = ['13-17', '18-24', '25-34', '35-44', '45-64'];
const NUMBER_OF_COUNTRIES = 5;

const CampaignIgAudienceData = ({
  audienceData,
  type = 'posts'
}: Props) => {
  const { data, isLoading } = audienceData;
  const { audienceAgeSplit = [], audienceGenderSplit = [], audienceTopCountries = [], total } = data || {};

  const getGraphSeriesData = () => {
    const audienceDataByAgeMap = audienceAgeSplit.reduce((result, item) => {
      // eslint-disable-next-line no-param-reassign
      result[item.label] = item;

      return result;
    }, {});

    return AUDIENCE_BY_AGE_X_AXIS_LABELS.map(xAxisLabel => {
      return {
        value: audienceDataByAgeMap[xAxisLabel]?.absolute,
        percentage: audienceDataByAgeMap[xAxisLabel]?.percentage.toFixed(2),
        ...AUDIENCE_BY_AGE_STYLE_MAPPING[xAxisLabel]
      };
    });
  };

  const getAudienceByCountriesChartData = (): Array<BarChartSimpleItem> => {
    return audienceTopCountries.slice(0, NUMBER_OF_COUNTRIES).map(({ label, absolute, percentage }) => ({
      label,
      additionalMetric: absolute,
      value: getMetricValueInPercentage(percentage, { defaultValue: '', hasThousandsSeparator: true }).replace('%', '')
    }));
  };

  const renderSpinner = (): JSX.Element => (
    <div className="campaign-ig-audience-data__loader">
      <Spinner
        position="inline"
        size="small"
        color="light"
      />
    </div>
  );

  const renderGenderBar = (): JSX.Element => {
    if (!isEmpty(audienceGenderSplit)) {
      return (
        <div className="campaign-ig-audience-data__audience-bar-container">
          <AudienceGenderBar
            labelType="text"
            valueStyle="strong"
            items={audienceGenderSplit}
            showAbsoluteValues
            tooltipWrapperClass="campaign-ig-audience-data__audience-gender-container"
          />
        </div>
      );
    }

    return (
      <EmptyMessage wrapperClass="campaign-ig-audience-data__empty-message-gender" />
    );
  };

  const renderBarChart = (): JSX.Element => (
    <BarChartSimple
      items={getAudienceByCountriesChartData()}
      titles={['Country', '', 'Followers', 'Total']}
      color="#26aafe"
      showPercentMarker
      hideBarColumn
      isAbbreviated
    />
  );

  const renderBarGraph = (): JSX.Element => {
    if (!isEmpty(audienceAgeSplit)) {
      return (
        <BarGraphLazy
          key="bar-graph-audience"
          data={getAudienceData(getGraphSeriesData(), AUDIENCE_BY_AGE_X_AXIS_LABELS)}
        />
      );
    }

    return (
      <EmptyMessage wrapperClass="campaign-ig-audience-data__empty-message-age" />
    );
  };

  const renderAudienceData = (): JSX.Element => {
    return (
      <div className="campaign-ig-audience-data">
        <div className="campaign-ig-audience-data__audience-graph-container">
          <div className="campaign-ig-audience-data__audience-graph-name">
            {__t('campaign_ig_audience_data.audience_graph_name')}
          </div>
          {
            isLoading
              ? renderSpinner()
              : renderBarGraph()
          }
        </div>
        <div className="campaign-ig-audience-data__audience-data">
          <div className="campaign-ig-audience-data__audience-by-gender">
            <div className="campaign-ig-audience-data__audience-title">
              {__t('campaign_ig_audience_data.audience_by_gender_title')}
            </div>
            {
              isLoading
                ? renderSpinner()
                : renderGenderBar()
            }
          </div>
          <div className="campaign-ig-audience-data__audience-by-country">
            <div className="campaign-ig-audience-data__audience-title">
              {__t('campaign_ig_audience_data.audience_by_country')}
            </div>
            <div className="campaign-ig-audience-data__audience-bar-chart">
              {
                isLoading
                  ? renderSpinner()
                  : renderBarChart()
              }
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderNoDataMessage = (): JSX.Element => {
    return (
      <div className="campaign-ig-audience-data__errored">
        {__t(`campaign_ig_audience_data.${isEmpty(data) ? 'audience_data_api_fail_message' : 'audience_no_data'}`, {
          capitalized: __t(`campaign_ig_audience_data.audience_${type}_capitalized`)
        })}
      </div>
    );
  };

  const hasData = Object.keys(data || {}).filter(audienceDataType => (data[audienceDataType] || []).length).length;

  return (
    <div className="campaign-ig-audience-data__container">
      <div className="campaign-ig-audience-data__ig-audience-data-title">
        {__t(isNil(total) ?
          'campaign_ig_audience_data.audience_data_title_without_reach' :
          'campaign_ig_audience_data.audience_data_title',
        { total: abbrNum(total, { decimal: 1 }, true) })}
        <span className="campaign-ig-audience-data__section-tooltip">
          <Tooltip
            message={__t('campaign_ig_audience_data.audience_data_tooltip', {
              lowercase: __t(`campaign_ig_audience_data.audience_${type}_lowercase`)
            })}
            showQuestionMark
          />
        </span>
      </div>
      {
        !isLoading && !hasData ? renderNoDataMessage() : renderAudienceData()
      }
    </div>
  );
};

export default CampaignIgAudienceData;
